import React, { useEffect } from "react";
import Lottie from "lottie-react";
import loadingAnimation from "@assets/loading_anim.json";
import { GenerationState } from "@pages/Template/utils/constants";
import { capitalizeWords } from "@utils/textUtils";
import { wsBaseUrl } from "@utils/baseValues";

interface TemplateGenerationProps {
  taskId: string | null;
  isTemplateGenerating: boolean;
  setIsTemplateGenerating: (value: boolean) => void;
  setIsGenerated: (value: boolean) => void;
  setTemplatePrompt: (value: string | null) => void;
  setGenerationStatus: (value: string | null) => void;
  generationStatus: string | null;
}

const TemplateGeneration: React.FC<TemplateGenerationProps> = ({
  taskId,
  isTemplateGenerating,
  setIsTemplateGenerating,
  setIsGenerated,
  setTemplatePrompt,
  setGenerationStatus,
  generationStatus,
}) => {
  useEffect(() => {
    if (!taskId) return;

    const ws = new WebSocket(`${wsBaseUrl}/task/${taskId}`);
    setIsTemplateGenerating(true);

    ws.onmessage = async (event) => {
      const message = JSON.parse(event.data);

      switch (message.state) {
        case GenerationState.SUCCESS:
          setGenerationStatus(GenerationState.SUCCESS);
          setIsGenerated(true);
          setTemplatePrompt(message.result);
          setIsTemplateGenerating(false);
          break;

        case GenerationState.PENDING:
          setGenerationStatus(GenerationState.PENDING);
          break;

        case GenerationState.PROGRESS:
          setGenerationStatus(message.current_step);
          break;

        case GenerationState.FAILURE:
          console.error("Template generation failed.");
          setIsTemplateGenerating(false);
          break;

        default:
          console.warn("Unknown state received:", message.state);
      }
    };

    ws.onclose = () => {
      console.log("WebSocket closed.");
      setIsTemplateGenerating(false);
    };

    return () => ws.close();
  }, [taskId]);

  if (!isTemplateGenerating) return null;

  return (
    <div className="max-w-2xl">
      <div className="flex flex-col items-center">
        <Lottie
          animationData={loadingAnimation}
          className="w-[250px] h-[250px] 2xl:w-[300px] 2xl:h-[300px] mb-4"
        />
        <p className="text-center text-sm 2xl:text-base font-medium mb-2">
          Just a moment,
          <br />
          the template is being generated.
        </p>
        <h1 className="text-[#98A2B3] text-sm 2xl:text-base">
          {generationStatus && `${capitalizeWords(generationStatus ?? "")}`}
          <span className="loading-dots w-[10px]" aria-hidden="true"></span>
        </h1>
      </div>
    </div>
  );
};

export default TemplateGeneration;
